import posthog from 'posthog-js'
import { redactUrl } from '@/lib/observability/redact'
import { installClientErrorReporter } from '@/lib/observability/adapters/posthog-browser'

/**
 * Client-side PostHog initialisation.
 *
 * ⚠️ **There used to be TWO of these** — this one and `/instrumentation-client.ts`
 * at the repo root — with different configs. Next resolves `src/` FIRST
 * (`next/dist/build/create-compiler-aliases.js`:
 * `['private-next-instrumentation-client']: [join(dir,'src',…), join(dir,…), empty]`),
 * so the ROOT file was dead code and the credential scrubber it contained had
 * never run once: the emitted chunk was `src_instrumentation-client_ts_*.js` and
 * contained zero occurrences of "redacted" — so commit `9a7e201` ("Scrubbed at
 * the source") had written the fix into the file Next does not load.
 *
 * ⚠️ **But no credential was ever actually captured** (verified against the live
 * project 2026-09-12: zero hits across 1,243 events and 82 sessions, and no
 * session ever reached `/update-password` or `/callback`). A reachable path, not
 * a realised exposure — an earlier version of this comment said otherwise. Both files' protections are merged
 * below and the root file is deleted. `scripts/verify/observability.mjs`
 * asserts that only one of these exists, so this cannot come back.
 *
 * ⚠️ **Corrected 2026-09-12 — the previous note here was wrong.** It claimed this
 * file "did not exist until 2026-08-21, and nothing client-side was being
 * recorded", and that nine files of `capture()` calls had been no-ops for four
 * months. **Analytics was working the whole time.** Queried against the live
 * project: the earliest event is `2026-07-09T12:20:05Z`, minutes after the
 * wizard commit `c321d5b` landed — 370 events in July alone, including
 * `login_submitted` and `$identify`, two of the nine supposedly dead events.
 *
 * What actually happened on 2026-08-22 (`db1ad91`) is that this file was created
 * and **shadowed the working root one**, because Next resolves `src/` first.
 * Analytics was not switched on that day; its configuration was silently moved
 * to a different file. That is also why the credential scrubber added to the
 * root file on 2026-08-25 never ran — by then the root file was already dead.
 *
 * `message_sent` therefore **does** have history (sparse: 2 events in August),
 * which matters for #55's rate cap — it was recorded as having none.
 *
 * `instrumentation-client.ts` is the Next 16 convention for this (see
 * `next/dist/docs/01-app/03-api-reference/03-file-conventions/instrumentation-client.md`);
 * it runs before the app becomes interactive, so events fired during first
 * render are not lost. That timing is also why the error reporter is installed
 * here: a boundary that fires before this file runs falls back to the port's
 * no-op and its `console.error`, which is the correct degradation.
 */
const token = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN

// The docs are explicit that instrumentation must not be able to break the app:
// "Implement try-catch blocks around your instrumentation code ... This prevents
// individual tracking failures from affecting other instrumentation features."
// Analytics is never worth a white screen.
try {
  if (token) {
    posthog.init(token, {
      // Where the BROWSER sends events and exceptions. A proxy, because ad
      // blockers maintain lists of known analytics domains and PostHog puts the
      // gain at "typically 10-30%" more events captured.
      //
      // Config, not code — the same reasoning as LIFECYCLE_EMAILS_DISABLED:
      // cutting over to the managed proxy, or rolling back off it, shouldn't
      // need a PR. The default is today's same-origin `/ingest` rewrite (still
      // in next.config.ts), so an unset variable changes nothing.
      //
      // ⚠️ NEXT_PUBLIC_* is inlined at `next build`, so changing this in Vercel
      // requires a REDEPLOY — untick "Use existing Build Cache". Editing the
      // variable alone does nothing. See reference/OPERATIONS.md §9.
      //
      // ⚠️ Deliberately NOT NEXT_PUBLIC_POSTHOG_HOST. That is the INGESTION
      // host (eu.i.posthog.com) and is server-side only — posthog-node and the
      // OTel logs exporter. Three variables, three non-overlapping jobs:
      // token = client + server · host = server only · proxy = client only.
      api_host: process.env.NEXT_PUBLIC_POSTHOG_PROXY ?? '/ingest',

      // ⚠️ The UI host, NOT NEXT_PUBLIC_POSTHOG_HOST. That variable is the
      // INGESTION host (eu.i.posthog.com); using it here produces "view in
      // PostHog" links that 404. This file used to get that wrong.
      ui_host: 'https://eu.posthog.com',

      // Error Tracking. This is the reason `productionBrowserSourceMaps` is on
      // and why the build uploads and then deletes source maps — without it,
      // every stack trace in PostHog would be minified and useless.
      capture_exceptions: true,

      // Next's App Router does not emit a full page load per navigation, so
      // automatic pageview capture misses client-side route changes. Left on
      // for the initial load; per-route capture is part of the analytics
      // taxonomy work (workstream I, → DECISIONS.md #20) rather than guessed at
      // here, because the event names are a contract with the KPI matrix.
      //
      // ⚠️ Deliberately NOT switched to the `defaults` preset. Setting
      // `defaults: '2026-01-30'` resolves `capture_pageview` to
      // `'history_change'` (posthog-js: `capture_pageview: !t || "2025-05-24" > t
      // || "history_change"`), which is a step change in $pageview volume that
      // would read as a traffic jump across the deploy date. That belongs with
      // #20, not with an error-tracking change.
      capture_pageview: true,

      // The credential backstop. See `@/lib/observability/redact` for what and
      // why; the same function runs on the server path in `instrumentation.ts`,
      // because `onRequestError` receives the query string too.
      before_send: (event) => {
        if (!event) return event
        for (const key of ['$current_url', '$referrer', '$pathname'] as const) {
          const value = event.properties?.[key]
          if (value) event.properties[key] = redactUrl(value)
        }
        return event
      },

      loaded: () => {
        // Do not fill the project with events from `pnpm dev`.
        if (process.env.NODE_ENV === 'development') posthog.opt_out_capturing()

        // Wire the error-reporting port. AFTER init, so the first thing the
        // reporter can be asked to do cannot hit an uninitialised SDK.
        installClientErrorReporter()
      },
    })
  } else if (process.env.NODE_ENV !== 'production') {
    console.warn(
      '[posthog] NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN is not set — analytics and error tracking are off.',
    )
  }
} catch (e) {
  console.warn('[posthog] init failed; continuing without analytics', e)
}
